import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useVouches } from '../hooks/useVouches';
import { useLinkedInImport } from '../hooks/useLinkedInImport';
import { useProfile } from '../hooks/useProfile';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { supabase } from '../lib/supabase';
import { VOUCH_REWARD } from '../brand/vouch/tokens';

const REJECT_REASONS = [
  'Experience level mismatch',
  'Skills not aligned',
  'Location requirements',
  'Salary expectations too high',
  'Not hiring right now',
];

export default function VoucherView({ onStats }) {
  const { user, profile } = useAuth();
  const { pendingRequests, myVouches, potentialJobsCount, approveVouch, rejectVouch, subscribeToRequests, fetchPendingRequests, fetchMyVouches } = useVouches();
  const { saveLinkedInData, loadDemoData } = useProfile();
  const linkedIn = useLinkedInImport();

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [vouchNote, setVouchNote] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [hmMessageMap, setHmMessageMap] = useState({});
  const [candidateNoteMap, setCandidateNoteMap] = useState({});
  const fileInputRef = useRef(null);

  // Compute derived data
  const connectedCount = myVouches.filter(v => v.job_interest?.status === 'connected').length;
  const thankedVouches = myVouches.filter(v => v.job_interest?.status === 'connected' || v.job_interest?.status === 'rejected');

  // Push stats to header
  useEffect(() => {
    if (onStats && profile?.linkedin_connected) {
      onStats({ role: 'voucher', potentialJobs: potentialJobsCount, matchCount: connectedCount, newRequests: pendingRequests.length });
    }
  }, [onStats, profile?.linkedin_connected, potentialJobsCount, connectedCount, pendingRequests.length]);

  // Fetch HM messages for thanked vouches via RPC (bypasses messages RLS,
  // since the HM message is sent to the candidate, not the voucher)
  const fetchHmMessages = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .rpc('get_voucher_thank_you_messages', { p_voucher_id: user.id });
    if (data) {
      const map = {};
      for (const msg of data) {
        if (!map[msg.job_interest_id]) {
          map[msg.job_interest_id] = msg;
        }
      }
      setHmMessageMap(map);
    }
  }, [user]);

  // Fetch when thanked vouches appear (use .length to avoid new-array-ref loop)
  useEffect(() => {
    if (thankedVouches.length === 0) return;
    // Small delay: the HM sends the message AFTER updating job_interest status,
    // so the realtime event may arrive before the message is inserted.
    const timer = setTimeout(fetchHmMessages, 800);
    return () => clearTimeout(timer);
  }, [thankedVouches.length, fetchHmMessages]);

  // Re-fetch when the Thank You panel is opened (catches messages that arrived
  // after the initial fetch triggered by the status change)
  useEffect(() => {
    if (showThankYou && thankedVouches.length > 0) {
      fetchHmMessages();
    }
  }, [showThankYou, thankedVouches.length, fetchHmMessages]);

  // Fetch candidate notes for pending requests
  useEffect(() => {
    if (!user || pendingRequests.length === 0) return;
    const jiIds = pendingRequests.map(r => r.id);
    supabase
      .from('messages')
      .select('job_interest_id, content')
      .in('job_interest_id', jiIds)
      .neq('sender_id', user.id)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach(m => { if (!map[m.job_interest_id]) map[m.job_interest_id] = m.content; });
          setCandidateNoteMap(map);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingRequests.length]);

  // Refetch when user switches back to this PWA
  useVisibilityRefresh(useCallback(() => {
    fetchPendingRequests();
    fetchMyVouches();
  }, [fetchPendingRequests, fetchMyVouches]));

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToRequests(() => {});
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async () => {
    if (!selectedRequest || !vouchNote.trim()) return;
    await approveVouch(
      selectedRequest.id,
      selectedRequest.candidate_id,
      selectedRequest.job_id,
      vouchNote.trim()
    );
    setSelectedRequest(null);
    setVouchNote('');
  };

  const handleReject = async (reason) => {
    if (!selectedRequest) return;
    await rejectVouch(selectedRequest.id, reason, selectedRequest.candidate_id);
    setSelectedRequest(null);
    setShowRejectModal(false);
  };

  const handleImportComplete = async () => {
    await saveLinkedInData({
      profile: linkedIn.importedProfile,
      positions: linkedIn.importedPositions,
      connections: linkedIn.importedConnections,
    });
    setShowImportModal(false);
    linkedIn.reset();
  };

  // LinkedIn Import Modal
  if (showImportModal) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-bold">Connect Your LinkedIn</h2>
              <button onClick={() => { setShowImportModal(false); linkedIn.reset(); }}
                className="text-white hover:text-cyan-200">
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="p-4">
            {linkedIn.importStep === 1 && (
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm text-gray-900 mb-2">How to export your LinkedIn data:</h3>
                  <ol className="text-xs text-gray-700 space-y-1.5 list-decimal list-inside">
                    <li>Go to LinkedIn Settings & Privacy</li>
                    <li>Click "Get a copy of your data"</li>
                    <li>Select the data you want (Connections, Profile)</li>
                    <li>Request the archive and download when ready</li>
                  </ol>
                </div>
                <button onClick={() => linkedIn.setImportStep(2)}
                  className="w-full py-3 bg-vouch-navy text-white rounded-lg font-semibold hover:bg-vouch-navy-light">
                  I Have My Data Export
                </button>
                <button onClick={async () => { await loadDemoData(); setShowImportModal(false); linkedIn.reset(); }}
                  className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 text-sm">
                  Load Demo Data Instead
                </button>
              </div>
            )}

            {linkedIn.importStep === 2 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                  <div className="text-4xl mb-2">📁</div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">Upload LinkedIn Export</p>
                  <p className="text-xs text-gray-500 mb-4">Drop your .zip file here</p>
                  <input ref={fileInputRef} type="file" accept=".zip" className="hidden"
                    onChange={(e) => e.target.files[0] && linkedIn.handleFileUpload(e.target.files[0])} />
                  <button onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-vouch-navy text-white rounded-lg text-sm font-semibold">
                    {linkedIn.loading ? 'Processing...' : 'Choose File'}
                  </button>
                </div>
                {linkedIn.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{linkedIn.error}</div>
                )}
                <button onClick={async () => { await loadDemoData(); setShowImportModal(false); linkedIn.reset(); }}
                  className="w-full py-2 text-gray-500 text-sm hover:text-gray-700">
                  Or load demo data instead
                </button>
              </div>
            )}

            {linkedIn.importStep === 3 && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 text-sm mb-2">Data Ready</h3>
                  {linkedIn.importedProfile && (
                    <p className="text-xs text-green-700">
                      Profile: {linkedIn.importedProfile.firstName} {linkedIn.importedProfile.lastName}
                    </p>
                  )}
                  <p className="text-xs text-green-700">{linkedIn.importedConnections.length} connections</p>
                </div>
                <button onClick={handleImportComplete}
                  className="w-full py-3 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg font-semibold">
                  Import & Continue
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not connected yet
  if (!profile?.linkedin_connected) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl p-6 shadow text-center">
          <div className="text-5xl mb-4">🤝</div>
          <h2 className="text-xl font-bold text-vouch-navy mb-2">Help People Land Great Roles</h2>
          <p className="text-gray-600 text-sm mb-6">
            Connect your LinkedIn to see when people in your network need a vouch.
          </p>
          <div className="bg-gradient-to-r from-slate-50 to-cyan-50 rounded-lg p-4 mb-6 border border-cyan-100">
            <div className="text-2xl font-bold text-vouch-navy blur-sm select-none">12 people</div>
            <p className="text-xs text-vouch-teal mt-1 font-medium">
              People in your network are looking for vouches
            </p>
          </div>
          <button onClick={() => setShowImportModal(true)}
            className="w-full py-3 bg-vouch-navy text-white rounded-lg font-semibold hover:bg-vouch-navy-light flex items-center justify-center gap-2">
            Connect Your Network
          </button>
          <button onClick={() => loadDemoData()}
            className="w-full mt-2 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 text-sm">
            Load Demo Data Instead
          </button>
        </div>
      </div>
    );
  }

  // Vouch request detail view
  if (selectedRequest) {
    const req = selectedRequest;
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <button onClick={() => { setSelectedRequest(null); setVouchNote(''); }}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
          ← Back to requests
        </button>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4 text-white">
            <h3 className="font-bold">Vouch Request</h3>
            <p className="text-cyan-100 text-sm">
              {req.candidate?.display_name} is interested in a role
            </p>
          </div>
          <div className="p-4 space-y-4">
            {/* Candidate info */}
            <div className="flex items-center gap-3">
              {req.candidate?.photo_url ? (
                <img src={req.candidate.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-vouch-teal" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-vouch-teal bg-opacity-20 flex items-center justify-center text-vouch-navy font-bold">
                  {req.candidate?.display_name?.[0]}
                </div>
              )}
              <div>
                <p className="font-semibold text-gray-900">{req.candidate?.display_name}</p>
                <p className="text-xs text-gray-500">{req.candidate?.current_title} at {req.candidate?.current_company}</p>
              </div>
            </div>

            {/* Job info */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">{req.jobs?.logo}</span>
                <div>
                  <p className="font-semibold text-sm">{req.jobs?.title}</p>
                  <p className="text-xs text-gray-500">{req.jobs?.company} - {req.jobs?.location}</p>
                </div>
              </div>
            </div>

            {/* Candidate's note */}
            {candidateNoteMap[req.id] && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-800 mb-1">
                  Message from {req.candidate?.display_name?.split(' ')[0]}:
                </p>
                <p className="text-sm text-blue-900 italic">"{candidateNoteMap[req.id]}"</p>
              </div>
            )}

            {/* Vouch note */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Your vouch for {req.candidate?.display_name?.split(' ')[0]}:
              </label>
              <textarea
                value={vouchNote}
                onChange={(e) => setVouchNote(e.target.value)}
                placeholder="I've worked with them and can say they're excellent at..."
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm h-28 resize-none focus:border-vouch-teal focus:outline-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={() => setShowRejectModal(true)}
                className="flex-1 bg-gray-100 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-200">
                <X size={16} /> Decline
              </button>
              <button onClick={handleApprove}
                disabled={!vouchNote.trim()}
                className="flex-1 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                <Check size={16} /> Vouch
              </button>
            </div>
          </div>
        </div>

        {/* Reject modal */}
        {showRejectModal && (
          <div className="bg-white rounded-xl shadow p-4">
            <h4 className="font-semibold text-sm mb-3">Why are you declining?</h4>
            {REJECT_REASONS.map(reason => (
              <button key={reason} onClick={() => handleReject(reason)}
                className="w-full text-left py-2 px-3 text-sm hover:bg-gray-50 rounded-lg">
                {reason}
              </button>
            ))}
            <button onClick={() => setShowRejectModal(false)}
              className="w-full mt-2 py-2 text-gray-500 text-sm">Cancel</button>
          </div>
        )}
      </div>
    );
  }

  // Main voucher view
  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Stats bar */}
      {profile?.linkedin_connected && (
        <div className="bg-blue-600 rounded-xl px-4 py-2.5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-100">
                <strong className="text-white">{potentialJobsCount} potential jobs</strong> • <strong className="text-white">{connectedCount} matches</strong>
              </p>
              <p className="text-xs text-blue-200 mt-0.5">
                Earn up to <strong className="text-white">${(connectedCount * VOUCH_REWARD).toLocaleString()}</strong>
              </p>
              {pendingRequests.length > 0 && (
                <p className="text-xs text-yellow-300 font-semibold mt-0.5">
                  ✨ {pendingRequests.length} new {pendingRequests.length === 1 ? 'request' : 'requests'}!
                </p>
              )}
            </div>
            {connectedCount > 0 && (
              <button
                onClick={() => setShowThankYou(!showThankYou)}
                className="flex items-center gap-1 bg-gradient-to-r from-green-400 to-emerald-500 px-2.5 py-1 rounded-full hover:from-green-500 hover:to-emerald-600 transition-all animate-pulse hover:animate-none"
              >
                <span className="text-sm">🎉</span>
                <span className="text-xs font-bold text-white">Thank You!</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Thank You Details */}
      {showThankYou && thankedVouches.length > 0 && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-3 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🎉</span>
              <h4 className="font-bold text-green-900 text-sm">Vouch Results ({thankedVouches.length})</h4>
            </div>
            <button onClick={() => setShowThankYou(false)} className="text-gray-500 hover:text-gray-700">
              <X size={16} />
            </button>
          </div>
          <div className="space-y-3">
            {thankedVouches.map(vouch => {
              const isConnected = vouch.job_interest?.status === 'connected';
              const hmName = vouch.jobs?.hiring_manager?.display_name || 'The Hiring Manager';
              const candidateName = vouch.candidate?.display_name || 'the candidate';
              const hmMsg = hmMessageMap[vouch.job_interest_id];
              return (
                <div key={vouch.id} className={`bg-white rounded-lg overflow-hidden border-2 ${isConnected ? 'border-green-300' : 'border-orange-200'}`}>
                  {/* Status header */}
                  <div className={`px-3 py-2 ${isConnected ? 'bg-green-100' : 'bg-orange-100'}`}>
                    <div className="flex items-center justify-between">
                      <p className={`text-xs font-bold ${isConnected ? 'text-green-800' : 'text-orange-800'}`}>
                        {isConnected
                          ? vouch.job_interest?.outcome === 'strong_fit' ? `🌟 Strong fit confirmed!`
                            : vouch.job_interest?.outcome === 'good_fit' ? `👍 Good fit — following up!`
                            : vouch.job_interest?.outcome === 'not_aligned' ? `The team moved in a different direction`
                            : vouch.job_interest?.outcome === 'underqualified' ? `The team is looking for different experience`
                            : vouch.job_interest?.outcome === 'other' ? `Outcome recorded`
                            : `${hmName} accepted your vouch!`
                          : `${hmName} passed on this candidate`}
                      </p>
                      <span className="text-sm">{isConnected ? (vouch.job_interest?.outcome ? (
                        vouch.job_interest.outcome === 'strong_fit' || vouch.job_interest.outcome === 'good_fit' ? '✅' : '📋'
                      ) : '✅') : '❌'}</span>
                    </div>
                  </div>

                  <div className="p-3 space-y-2">
                    {/* What you vouched for */}
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-xs text-gray-500 mb-1.5">Your vouch for:</p>
                      <div className="flex items-center gap-2">
                        {vouch.candidate?.photo_url ? (
                          <img src={vouch.candidate.photo_url} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-300" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-vouch-teal bg-opacity-20 flex items-center justify-center text-vouch-navy text-xs font-bold">
                            {vouch.candidate?.display_name?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-sm text-gray-900">{candidateName}</p>
                          <p className="text-xs text-gray-600">
                            {vouch.jobs?.title} at {vouch.jobs?.company}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* HM message */}
                    {hmMsg && (
                      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          {vouch.jobs?.hiring_manager?.photo_url ? (
                            <img src={vouch.jobs.hiring_manager.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-vouch-navy flex items-center justify-center text-white text-[8px] font-bold">
                              {hmName[0]}
                            </div>
                          )}
                          <p className="text-xs font-semibold text-gray-700">{hmMsg.sender_name || hmName}</p>
                        </div>
                        <p className="text-sm text-gray-900 italic pl-6">"{hmMsg.content}"</p>
                      </div>
                    )}

                    {/* Earnings / feedback */}
                    <div className={`${isConnected ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'} rounded-lg p-2 border`}>
                      {isConnected ? (
                        vouch.job_interest?.outcome === 'strong_fit' || vouch.job_interest?.outcome === 'good_fit' ? (
                          <p className="text-xs text-green-800">
                            <strong>🚀 Advancing!</strong> ${VOUCH_REWARD.toLocaleString()} if this leads to a hire!
                          </p>
                        ) : vouch.job_interest?.outcome && vouch.job_interest.outcome !== 'strong_fit' && vouch.job_interest.outcome !== 'good_fit' ? (
                          <p className="text-xs text-gray-600">
                            <strong>📋 Outcome recorded.</strong> Thanks for your vouch!
                          </p>
                        ) : (
                          <p className="text-xs text-green-800">
                            <strong>💰 Potential Earnings:</strong> ${VOUCH_REWARD.toLocaleString()} if this leads to a hire!
                          </p>
                        )
                      ) : (
                        <p className="text-xs text-orange-700">💡 This feedback helps you make better matches!</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending requests */}
      {pendingRequests.length > 0 ? (
        <>
          <div className="bg-gradient-to-r from-green-50 to-cyan-50 border border-green-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <img src="/brand/vouch/assets/vector/vouch-icon.svg" alt="" className="w-5 h-5" />
              <span className="font-bold text-sm text-green-800">Vouch Requests</span>
            </div>
            <p className="text-xs text-green-600">
              {pendingRequests.length} {pendingRequests.length === 1 ? 'person' : 'people'} in your network {pendingRequests.length === 1 ? 'wants' : 'want'} your endorsement
            </p>
          </div>

          {pendingRequests.map(req => (
            <div key={req.id} className="bg-white rounded-xl shadow overflow-hidden">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  {req.candidate?.photo_url ? (
                    <img src={req.candidate.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-vouch-teal bg-opacity-20 flex items-center justify-center text-vouch-navy font-bold text-sm">
                      {req.candidate?.display_name?.[0]}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{req.candidate?.display_name}</p>
                    <p className="text-xs text-gray-500">{req.candidate?.headline}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span>{req.jobs?.logo}</span>
                    <div>
                      <p className="text-sm font-medium">{req.jobs?.title}</p>
                      <p className="text-xs text-gray-500">{req.jobs?.company}</p>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedRequest(req)}
                  className="w-full py-2 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg text-sm font-semibold">
                  Review & Vouch
                </button>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow text-center">
          <div className="text-4xl mb-3">🤝</div>
          <h3 className="font-bold text-gray-900 mb-2">No Pending Requests</h3>
          <p className="text-sm text-gray-600">
            When people in your network express interest in jobs, their vouch requests will appear here.
          </p>
        </div>
      )}

      {/* Past vouches */}
      {myVouches.length > 0 && (
        <div className="bg-white rounded-xl p-4 shadow">
          <h4 className="font-semibold text-sm text-gray-900 mb-3">Your Vouches</h4>
          <div className="space-y-2">
            {myVouches.map(vouch => (
              <div key={vouch.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium">{vouch.candidate?.display_name}</p>
                  <p className="text-xs text-gray-500">{vouch.jobs?.title} at {vouch.jobs?.company}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    vouch.job_interest?.status === 'connected' ? 'bg-green-100 text-green-800' :
                    vouch.job_interest?.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {vouch.job_interest?.status === 'connected' ? 'Connected!' :
                     vouch.job_interest?.status === 'rejected' ? 'Passed' :
                     'Under review'}
                  </span>
                  {vouch.job_interest?.outcome && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      vouch.job_interest.outcome === 'strong_fit' ? 'bg-green-100 text-green-800' :
                      vouch.job_interest.outcome === 'good_fit' ? 'bg-blue-100 text-blue-800' :
                      vouch.job_interest.outcome === 'not_aligned' ? 'bg-orange-100 text-orange-800' :
                      vouch.job_interest.outcome === 'underqualified' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {vouch.job_interest.outcome === 'strong_fit' ? '🌟 Strong Fit' :
                       vouch.job_interest.outcome === 'good_fit' ? '👍 Good Fit' :
                       vouch.job_interest.outcome === 'not_aligned' ? '↔️ Not Aligned' :
                       vouch.job_interest.outcome === 'underqualified' ? '📊 Underqualified' :
                       '💬 Other'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
